import { Paper, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserService } from 'layouts/pages/System/users/services/user.service';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { UserHook } from './hooks/user.hook';
import { UserFilter } from './components/user.filter';
import { UserTable } from './components/user.table';
import { UserModal } from './components/user.modal';

export const Users = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const service = new UserService();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchFilter, setSearchFilter] = useState('');
    const [filterFinal, setFilterFinal] = useState('');
    const [refresh, setRefresh] = useState(true);
    const [total, setTotal] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<UserResponse | null>(null);

    // orderBy vazio de propósito: a API ordena por nome; mandar um campo criaria controle inócuo.
    const { json } = new UserHook({
        page,
        limit: rowsPerPage,
        orderBy: '',
        orderDirection: 'asc',
        filter: filterFinal,
        refresh,
        service,
    }).getJson();

    useEffect(() => {
        setTotal(json?.data?.totalItens ?? 0);
    }, [json]);

    const search = () => {
        const term = searchFilter.trim();

        setPage(0);
        setFilterFinal(term ? `?${new URLSearchParams({ Search: term }).toString()}` : '');
    };

    const clearSearch = () => {
        setSearchFilter('');
        setFilterFinal('');
        setRefresh((p) => !p);
    };

    const getAll = () => setRefresh((p) => !p);

    const handleModal = () => {
        if (openModal) setEditItem(null);
        setOpenModal((p) => !p);
    };

    // A lista já traz o que o formulário precisa e a API não busca por id: edita-se pela linha.
    const editAction = (id: string) => {
        const user = json?.data?.records.find((item) => item.id === id);

        if (!user) return;

        setEditItem(user);
        setOpenModal(true);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('usuarios.titulo')} />
            <Grid container spacing={1}>
                <UserFilter
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    onSearch={search}
                    onClear={clearSearch}
                    onEnter={(e) => e.key === 'Enter' && search()}
                />
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <MDButton
                        variant="gradient"
                        color={sidenavColor}
                        onClick={handleModal}
                        sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}
                    >
                        {t('general.adicionar')}
                    </MDButton>
                </Grid>
            </Grid>
            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <UserTable
                    data={json}
                    editAction={editAction}
                    refresh={refresh}
                    setRefresh={setRefresh}
                />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </Paper>
            <UserModal
                open={openModal}
                edit={editItem}
                handleModal={handleModal}
                getAll={getAll}
                clearSearch={clearSearch}
            />
        </DashboardLayout>
    );
};
